import { Either } from "functype";
import { apiError } from "../fp-utils.js";
/** Slots in the order they happen in a day; every list of slots is kept in this order */
export const DIGEST_SLOTS = ["morning", "midday", "evening"];
/** Local time of each slot, in the job's own timezone */
export const SLOT_TIMES = { morning: "08:00", midday: "12:00", evening: "17:00" };
export const SLOT_LABELS = { morning: "Morning", midday: "Midday", evening: "End of day" };
/** SLOT_TIMES as people read them: "8 AM", "12 PM", "5 PM" */
export const SLOT_CLOCK_LABELS = { morning: "8 AM", midday: "12 PM", evening: "5 PM" };
export const REACHOUT_JOB_TYPES = ["task_digest", "deadline_alert"];
export const isReachoutJobType = (jobType) => REACHOUT_JOB_TYPES.includes(jobType);
/** "morning+evening", or "weekly" */
export const scheduleOptionId = (schedule) => schedule.kind === "weekly" ? "weekly" : schedule.slots.join("+");
/** "Morning", "Morning + end of day", "Morning, midday + end of day", "Weekly (Monday morning)" */
export const scheduleLabel = (schedule) => {
    if (schedule.kind === "weekly")
        return "Weekly (Monday morning)";
    const labels = schedule.slots.map((slot, i) => (i === 0 ? SLOT_LABELS[slot] : SLOT_LABELS[slot].toLowerCase()));
    if (labels.length === 1)
        return labels[0] ?? "";
    return `${labels.slice(0, -1).join(", ")} + ${labels[labels.length - 1] ?? ""}`;
};
/** "8 AM", "8 AM and 5 PM", "8 AM, 12 PM and 5 PM" */
const clockList = (slots) => {
    const times = slots.map((slot) => SLOT_CLOCK_LABELS[slot]);
    if (times.length < 2)
        return times[0] ?? "";
    return `${times.slice(0, -1).join(", ")} and ${times[times.length - 1] ?? ""}`;
};
/** When a schedule sends, with times: "Daily at 8 AM and 5 PM", "Mondays at 8 AM" */
export const scheduleSummary = (schedule) => schedule.kind === "weekly" ? `Mondays at ${SLOT_CLOCK_LABELS.morning}` : `Daily at ${clockList(schedule.slots)}`;
const option = (schedule) => ({
    id: scheduleOptionId(schedule),
    label: scheduleLabel(schedule),
    schedule,
});
const daily = (...slots) => ({ kind: "daily", slots });
/**
 * Every option each reachout offers. A digest may run at any mix of the three slots, or weekly.
 * A deadline alert checks at all three and emails only what is new (a task due today, or newly
 * overdue), so there is nothing to pick.
 */
export const SCHEDULE_OPTIONS = {
    task_digest: [
        option(daily("morning")),
        option(daily("midday")),
        option(daily("evening")),
        option(daily("morning", "midday")),
        option(daily("morning", "evening")),
        option(daily("midday", "evening")),
        option(daily("morning", "midday", "evening")),
        option({ kind: "weekly" }),
    ],
    deadline_alert: [option(daily("morning", "midday", "evening"))],
};
/** The option a new reachout starts on: a morning digest, or the alert's three checks */
export const defaultSchedule = (jobType) => SCHEDULE_OPTIONS[jobType][0]?.schedule ?? daily("morning");
/** Scheduled reachouts are a Pro feature: Pro may pick any option, Free none */
export const isScheduleAllowed = (_jobType, tier) => tier === "pro";
const isSlot = (value) => DIGEST_SLOTS.includes(value);
/** Reads a schedule from a request body; slots come back in day order */
export const parseReachoutSchedule = (value) => {
    if (typeof value !== "object" || value === null) {
        return Either.left(apiError("BAD_REQUEST", "schedule must be an object"));
    }
    const candidate = value;
    if (candidate.kind === "weekly")
        return Either.right({ kind: "weekly" });
    if (candidate.kind !== "daily") {
        return Either.left(apiError("BAD_REQUEST", `schedule.kind must be "daily" or "weekly"`));
    }
    if (!Array.isArray(candidate.slots) || candidate.slots.length === 0 || !candidate.slots.every(isSlot)) {
        return Either.left(apiError("BAD_REQUEST", `schedule.slots must list one or more of ${DIGEST_SLOTS.join(", ")}`));
    }
    const slots = DIGEST_SLOTS.filter((slot) => candidate.slots.includes(slot));
    if (slots.length !== candidate.slots.length) {
        return Either.left(apiError("BAD_REQUEST", "schedule.slots must not repeat a slot"));
    }
    return Either.right({ kind: "daily", slots });
};
/**
 * A schedule this reachout offers and this plan may pick. `tier` is undefined for a caller who
 * skips plan limits (an admin with admin mode on).
 */
export const validateReachoutSchedule = (jobType, tier, value) => parseReachoutSchedule(value).flatMap((schedule) => {
    const offered = SCHEDULE_OPTIONS[jobType].some((o) => o.id === scheduleOptionId(schedule));
    if (!offered) {
        return Either.left(apiError("BAD_REQUEST", `${jobType} does not offer "${scheduleLabel(schedule)}"`));
    }
    if (tier !== undefined && !isScheduleAllowed(jobType, tier)) {
        return Either.left(apiError("FORBIDDEN", `Scheduled emails need Agent Todo Pro (your plan: ${tier})`));
    }
    return Either.right(schedule);
});
export const scheduleJobFields = (schedule) => schedule.kind === "weekly"
    ? { scheduleType: "time_of_day", timeOfDay: SLOT_TIMES.morning, frequency: "weekly", slots: ["morning"] }
    : {
        scheduleType: "time_of_day",
        timeOfDay: SLOT_TIMES[schedule.slots[0] ?? "morning"],
        frequency: "daily",
        slots: schedule.slots,
    };
/** Slots stored in a job's config, in day order, or none */
export const slotsFromConfig = (config) => {
    const slots = typeof config === "object" && config !== null ? config.slots : undefined;
    return Array.isArray(slots) ? DIGEST_SLOTS.filter((slot) => slots.includes(slot)) : [];
};
/**
 * The schedule a stored job runs on, or undefined for a job set up another way (an interval an
 * admin chose while skipping plan limits).
 */
export const scheduleFromJob = (job) => {
    if (job.scheduleType !== "time_of_day")
        return undefined;
    if (job.frequency === "weekly")
        return { kind: "weekly" };
    if (job.frequency !== "daily")
        return undefined;
    const slots = slotsFromConfig(job.config);
    return slots.length > 0 ? { kind: "daily", slots } : undefined;
};
//# sourceMappingURL=reachout-schedule.js.map